/**
 * Stable accent colors for research category tags.
 * Token names map to `--color-cat-*` in theme.css — never hardcode hex in UI.
 */

export const RESEARCH_CATEGORY_COLOR_KEYS = [
  "tradition",
  "language",
  "instruments",
  "place",
  "moment",
  "people",
  "afterlife",
] as const;

export type ResearchCategoryColorKey = (typeof RESEARCH_CATEGORY_COLOR_KEYS)[number];

const FALLBACK_CYCLE = [
  "tradition",
  "language",
  "instruments",
  "place",
  "moment",
  "people",
  "afterlife",
] as const satisfies readonly ResearchCategoryColorKey[];

/** Resolve a category name to a theme color key. */
export function researchCategoryColorKey(name: string | null | undefined): ResearchCategoryColorKey | "default" {
  if (!name) return "default";
  const key = name.trim().toLowerCase();
  if ((RESEARCH_CATEGORY_COLOR_KEYS as readonly string[]).includes(key)) {
    return key as ResearchCategoryColorKey;
  }
  // Stable hash → cycle for discovered / custom categories.
  let hash = 0;
  for (let i = 0; i < key.length; i++) hash = (hash * 31 + key.charCodeAt(i)) >>> 0;
  return FALLBACK_CYCLE[hash % FALLBACK_CYCLE.length]!;
}

/** Tailwind text/bg/border utilities for a category chip. */
export function researchCategoryClasses(name: string | null | undefined): {
  text: string;
  bg: string;
  border: string;
  bar: string;
} {
  const key = researchCategoryColorKey(name);
  const map = {
    tradition: {
      text: "text-cat-tradition",
      bg: "bg-cat-tradition/15",
      border: "border-cat-tradition/40",
      bar: "bg-cat-tradition",
    },
    language: {
      text: "text-cat-language",
      bg: "bg-cat-language/15",
      border: "border-cat-language/40",
      bar: "bg-cat-language",
    },
    instruments: {
      text: "text-cat-instruments",
      bg: "bg-cat-instruments/15",
      border: "border-cat-instruments/40",
      bar: "bg-cat-instruments",
    },
    place: {
      text: "text-cat-place",
      bg: "bg-cat-place/15",
      border: "border-cat-place/40",
      bar: "bg-cat-place",
    },
    moment: {
      text: "text-cat-moment",
      bg: "bg-cat-moment/15",
      border: "border-cat-moment/40",
      bar: "bg-cat-moment",
    },
    people: {
      text: "text-cat-people",
      bg: "bg-cat-people/15",
      border: "border-cat-people/40",
      bar: "bg-cat-people",
    },
    afterlife: {
      text: "text-cat-afterlife",
      bg: "bg-cat-afterlife/15",
      border: "border-cat-afterlife/40",
      bar: "bg-cat-afterlife",
    },
    default: {
      text: "text-cat-default",
      bg: "bg-cat-default/15",
      border: "border-cat-default/40",
      bar: "bg-cat-default",
    },
  } as const;
  return map[key];
}
